/**
 * Datas no horário de Brasília — GUPM-332
 * ---------------------------------------
 * O contrato do app é o fuso da BARRAGEM (Patos de Minas/MG), não o da máquina de
 * quem lê: numa VM/kiosk de sala de controle em UTC o navegador erra 3 h (mesma
 * classe do GUPM-234, que fixou o fuso no horário de cobertura).
 *
 * Dois formatos, dois tratamentos:
 *  - **dia de calendário** (`YYYY-MM-DD`, ex.: prazo de ação) não tem hora nem fuso.
 *    `new Date('2026-08-05')` é meia-noite UTC e em Brasília vira 04/08 — por isso
 *    ele é formatado como TEXTO, nunca passa por `Date`;
 *  - **instante** (timestamptz) é convertido explicitamente para Brasília.
 *
 * `periodo.ts` (filtros da Auditoria e do Histórico de Trocas) segue no fuso da
 * máquina — é outro contrato, fora deste card.
 *
 * TS puro: sem React, sem Supabase, sem DOM.
 */

/** Fuso da UPM. Sem horário de verão no Brasil desde 2019. */
export const FUSO_BRASILIA = 'America/Sao_Paulo';

const PARTES_DO_DIA = new Intl.DateTimeFormat('en-US', {
  timeZone: FUSO_BRASILIA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const INSTANTE_PT_BR = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_BRASILIA,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const DIA_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Dia `YYYY-MM-DD` em Brasília no instante dado. `toISOString().slice(0, 10)` corta
 * em UTC: depois das 21h daqui já devolve "amanhã".
 */
export function diaEmBrasilia(instante: Date): string {
  const partes = PARTES_DO_DIA.formatToParts(instante);
  const valor = (tipo: Intl.DateTimeFormatPartTypes) => partes.find((p) => p.type === tipo)?.value ?? '';
  return `${valor('year')}-${valor('month')}-${valor('day')}`;
}

/** Soma `n` dias a um dia de calendário `YYYY-MM-DD` — aritmética de calendário, sem fuso. */
export function somarDiasAoDia(dia: string, n: number): string {
  const d = new Date(`${dia}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * `YYYY-MM-DD` → `dd/mm/aaaa`, como texto. Entrada que não é dia de calendário volta
 * como veio — nunca "Invalid Date" nem uma data inventada.
 */
export function formatarDiaBr(dia: string): string {
  const m = DIA_ISO.exec(dia);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dia;
}

/** Instante (ISO 8601) → `dd/mm/aaaa, hh:mm:ss` em Brasília. Instante inválido → `—`. */
export function formatarInstanteBrasilia(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : INSTANTE_PT_BR.format(d);
}

/** Instante (ISO 8601) → só o DIA `dd/mm/aaaa` em Brasília. Instante inválido → `—`. */
export function formatarDiaDoInstante(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : formatarDiaBr(diaEmBrasilia(d));
}
